import { Button } from '@ophelos/ui/components/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@ophelos/ui/components/dialog';
import { Drawer, DrawerContent, DrawerHeader, DrawerTitle } from '@ophelos/ui/components/drawer';
import { ListChecks, PieChart, SlidersHorizontal } from 'lucide-react';
import { useState } from 'react';
import { PaymentOptionRow } from '@/components/payments/PaymentOptionRow';
import { useIsMobile } from '@/hooks/use-mobile';
import { useTranslation } from '@/hooks/useTranslation';
import type { PaymentOptionsPlacement } from '@/types';

export interface PaymentOptionsProps {
  /** Absent when the operator does not offer a plan on this balance, or a session in flight blocks one. */
  onChoosePlan?: () => void;
  /** Absent when the operator does not offer a part payment on this balance, or a session in flight blocks one. */
  onChoosePartial?: () => void;
  /** Live plan discount, shown beside the plan row's title. */
  planDiscountLabel?: string | null;
  /** 'inline' lists the rows under the amount; 'menu' keeps them behind an Options button. */
  placement?: PaymentOptionsPlacement;
}

/**
 * The other ways of paying a balance, shared by the case and account pay pages. `rows` goes under
 * the amount in the inline placement; `button` goes in the bottom bar and `menu` beside it in the
 * menu placement. Each is null when it has nothing to show.
 */
export function usePaymentOptions({ onChoosePlan, onChoosePartial, planDiscountLabel = null, placement = 'inline' }: PaymentOptionsProps) {
  const { t } = useTranslation();
  const isMobile = useIsMobile();
  const [open, setOpen] = useState(false);
  const hasOptions = !!(onChoosePlan || onChoosePartial);

  // Closes the menu before handing over, so it is not left open under the view that replaces this one.
  const choose = (handler: () => void) => () => {
    setOpen(false);
    handler();
  };
  // The same upright buttons whether they sit under the balance or behind the Options button.
  const rows = (
    <div className="grid grid-flow-col auto-cols-fr gap-5 w-fit mx-auto">
      {onChoosePlan && (
        <PaymentOptionRow
          icon={ListChecks}
          label={t('payment.paymentPlan')}
          onClick={choose(onChoosePlan)}
          data-testid="payment-action-plan"
          badge={
            planDiscountLabel && (
              <span className="highlight-amber rounded-full px-2 py-0.5 text-xs font-semibold whitespace-nowrap">{planDiscountLabel}</span>
            )
          }
        />
      )}
      {onChoosePartial && (
        <PaymentOptionRow
          icon={PieChart}
          label={t('payment.partialPayment')}
          onClick={choose(onChoosePartial)}
          data-testid="payment-action-partial"
        />
      )}
    </div>
  );

  if (!hasOptions) return { rows: null, button: null, menu: null };
  if (placement === 'inline') {
    return { rows: <div className="mt-6">{rows}</div>, button: null, menu: null };
  }
  return {
    rows: null,
    button: (
      <Button
        variant="outline"
        size="icon-lg"
        className="rounded-lg border-border-heavy size-12"
        onClick={() => setOpen(true)}
        aria-label={t('paymentTabs.options')}
        data-testid="payment-options-button"
      >
        <SlidersHorizontal className="size-4.5" />
      </Button>
    ),
    menu: isMobile ? (
      <Drawer open={open} onOpenChange={setOpen}>
        <DrawerContent className="bg-card">
          <DrawerHeader className="text-left">
            <DrawerTitle>{t('paymentTabs.optionsTitle')}</DrawerTitle>
          </DrawerHeader>
          <div className="px-4 pb-8">{rows}</div>
        </DrawerContent>
      </Drawer>
    ) : (
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="sm:max-w-md rounded-2xl px-6">
          <DialogHeader className="py-2">
            <DialogTitle>{t('paymentTabs.optionsTitle')}</DialogTitle>
          </DialogHeader>
          <div className="py-2">{rows}</div>
        </DialogContent>
      </Dialog>
    ),
  };
}
